import React, { useState, useEffect, useMemo } from "react";
import { useDispatch } from "react-redux";
import { useTranslation } from "react-i18next";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  IconButton,
  Button,
  CircularProgress,
} from "@material-ui/core";
import { TextBox, NumberBox } from "devextreme-react";
import CustomSelectBox from "../../common/CustomSelectBox";
import closeIcon from "../../../images/closeIcon.svg";
import { baseRequest } from "../../../fetch/basicThunk";
import {
  QUERY_POST_BILLING_CREATE_PAYMENT_ORDER,
  QUERY_PUT_BILLING_UPDATE_PAYMENT_ORDER,
} from "../../../fetch/constantQueries";
import { openModal } from "../../../redux/modal/modalSlice";

export default function CreatePaymentOrderModal({
  open,
  caseId,
  orderToEdit,
  onClose,
  onSuccess,
}) {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const [rubricType, setRubricType] = useState("BASE_RATE");
  const [description, setDescription] = useState(() =>
    t("BillingPaymentOrders.RubricBaseRate")
  );
  const [quantity, setQuantity] = useState(1);
  const [unitPrice, setUnitPrice] = useState(null);
  const [loading, setLoading] = useState(false);

  const rubricOptions = useMemo(
    () => [
      { id: "BASE_RATE", text: t("BillingPaymentOrders.RubricBaseRate") },
      { id: "EXCESS_KM", text: t("BillingPaymentOrders.RubricExcessKm") },
      { id: "TOLL", text: t("BillingPaymentOrders.RubricToll") },
      { id: "STOP_HOUR", text: t("BillingPaymentOrders.RubricStopHour") },
      { id: "SKATES", text: t("BillingPaymentOrders.RubricSkates") },
      { id: "DISPATCH_FEE", text: t("BillingPaymentOrders.RubricDispatchFee") },
      { id: "OTHER", text: t("BillingPaymentOrders.RubricOther") },
    ],
    [t]
  );

  const getRubricDefaultDescription = (type) => {
    switch (type) {
      case "BASE_RATE":
        return t("BillingPaymentOrders.RubricBaseRate");
      case "EXCESS_KM":
        return t("BillingPaymentOrders.RubricExcessKm");
      case "TOLL":
        return t("BillingPaymentOrders.RubricToll");
      case "STOP_HOUR":
        return t("BillingPaymentOrders.RubricStopHour");
      case "SKATES":
        return t("BillingPaymentOrders.RubricSkates");
      case "DISPATCH_FEE":
        return t("BillingPaymentOrders.RubricDispatchFee");
      case "OTHER":
      default:
        return "";
    }
  };

  useEffect(() => {
    if (open) {
      if (orderToEdit) {
        setRubricType(orderToEdit.rubricType || "OTHER");
        setDescription(orderToEdit.description || "");
        setQuantity(orderToEdit.quantity != null ? orderToEdit.quantity : 1);
        setUnitPrice(orderToEdit.unitPrice != null ? orderToEdit.unitPrice : null);
      } else {
        setRubricType("BASE_RATE");
        setDescription(t("BillingPaymentOrders.RubricBaseRate"));
        setQuantity(1);
        setUnitPrice(null);
      }
      setLoading(false);
    }
  }, [open, orderToEdit]);

  const handleRubricChange = (e) => {
    const selected = e.value;
    setRubricType(selected);
    if (!orderToEdit) {
      setDescription(getRubricDefaultDescription(selected));
    }
  };

  const numQuantity = Number(quantity) || 0;
  const numUnitPrice = unitPrice !== "" && unitPrice !== null ? Number(unitPrice) : null;
  const totalValue =
    numUnitPrice != null
      ? Math.round(numQuantity * numUnitPrice * 100) / 100
      : 0;

  const isFormValid =
    description.trim().length > 0 &&
    numQuantity > 0 &&
    numUnitPrice != null &&
    numUnitPrice >= 0;

  const handleSubmit = async () => {
    if (!isFormValid || loading || (!caseId && !orderToEdit)) return;

    setLoading(true);
    try {
      const payload = {
        rubricType,
        description: description.trim(),
        quantity: numQuantity,
        unitPrice: numUnitPrice,
        totalValue,
      };

      const result = orderToEdit
        ? await baseRequest(
            { param: `${orderToEdit.id}`, body: payload },
            dispatch,
            QUERY_PUT_BILLING_UPDATE_PAYMENT_ORDER,
            "PUT"
          )
        : await baseRequest(
            { param: `${caseId}/payment-orders`, body: payload },
            dispatch,
            QUERY_POST_BILLING_CREATE_PAYMENT_ORDER,
            "POST"
          );

      setLoading(false);
      if (onSuccess) {
        onSuccess(result, Boolean(orderToEdit));
      }
      onClose();
    } catch (err) {
      setLoading(false);
      dispatch(
        openModal({
          message: orderToEdit
            ? t("BillingPaymentOrders.ErrorEditOrder")
            : t("BillingPaymentOrders.ErrorCreateOrder"),
          type: "error",
        })
      );
    }
  };

  return (
    <Dialog
      PaperProps={{ square: true }}
      maxWidth="sm"
      open={open}
      onClose={loading ? undefined : onClose}
    >
      <div style={{ width: "520px", maxWidth: "100%" }}>
        <DialogTitle disableTypography className="titleStyles">
          <span>
            {orderToEdit
              ? t("BillingPaymentOrders.EditModalTitle")
              : t("BillingPaymentOrders.CreateModalTitle")}
          </span>
          <IconButton
            size="small"
            onClick={onClose}
            disabled={loading}
            aria-label="close"
          >
            <img
              height={15}
              src={closeIcon}
              alt=""
              title={t("BillingPaymentOrders.BtnCancel")}
            />
          </IconButton>
        </DialogTitle>
        <Divider style={{ backgroundColor: "#000" }} orientation="horizontal" />
        <DialogContent style={{ padding: "1.5rem" }}>
          <div className="edit-values-wrapper">
            <CustomSelectBox
              label={t("BillingPaymentOrders.FieldRubricType")}
              labelMode="floating"
              dataSource={rubricOptions}
              displayExpr="text"
              valueExpr="id"
              value={rubricType}
              onValueChanged={handleRubricChange}
              disabled={loading}
            />

            <TextBox
              label={t("BillingPaymentOrders.FieldDescription")}
              labelMode="floating"
              value={description}
              onValueChanged={(e) => setDescription(e.value || "")}
              disabled={loading}
            />

            <div style={{ display: "flex", gap: "1rem" }}>
              <div style={{ flex: 1 }}>
                <NumberBox
                  label={t("BillingPaymentOrders.FieldQuantity")}
                  labelMode="floating"
                  value={quantity}
                  min={0.01}
                  step={1}
                  onValueChanged={(e) => setQuantity(e.value)}
                  disabled={loading}
                />
              </div>
              <div style={{ flex: 1 }}>
                <NumberBox
                  label={t("BillingPaymentOrders.FieldUnitPrice")}
                  labelMode="floating"
                  value={unitPrice}
                  format="R$ #,##0.00"
                  min={0}
                  step={0.01}
                  onValueChanged={(e) => setUnitPrice(e.value)}
                  disabled={loading}
                />
              </div>
            </div>

            <NumberBox
              label={t("BillingPaymentOrders.FieldTotalValue")}
              labelMode="floating"
              value={totalValue}
              format="R$ #,##0.00"
              readOnly={true}
              disabled={true}
            />
          </div>
        </DialogContent>
        <DialogActions
          style={{
            padding: "0.5rem 1.5rem 1.5rem 1.5rem",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Button
            onClick={onClose}
            color="primary"
            variant="outlined"
            disabled={loading}
          >
            {t("BillingPaymentOrders.BtnCancel")}
          </Button>
          <Button
            onClick={handleSubmit}
            color="primary"
            variant="contained"
            disabled={!isFormValid || loading}
          >
            {loading ? (
              <CircularProgress size={20} color="inherit" />
            ) : (
              t("BillingPaymentOrders.BtnSaveOrder")
            )}
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
